import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { Layer, Source } from "react-map-gl";
import { useAuthStore, useMapStore } from "../..";
import { ILayerWithAbilityToOverrideIsStyleLoadedProps } from "../../interfaces/IlayerProps";
import GoogleTilesService from "../../services/googleTilesService";
import LocalStorageService from "../../services/localStorageService";

function SatelliteGoogleBaseMapLayer({ visible, isStyleLoadedOverride }: ILayerWithAbilityToOverrideIsStyleLoadedProps) {
    const authStore = useAuthStore();
    const mapStore = useMapStore();
    const localStorageService = useMemo(() => new LocalStorageService(), []);
    const googleTilesService = useMemo(() => new GoogleTilesService(), []);
    const [sessionToken, setSessionToken] = useState<string | null>(null);

    useEffect(() => {
        if (!(isStyleLoadedOverride ?? mapStore.isStyleLoaded))
            return;

        let isRequestCancelled = false;

        (async () => {
            const storedSession = localStorageService.getGoogleTilesSession();
            const isStoredSessionValid = storedSession && storedSession.expiry * 1000 > Date.now();

            if (isStoredSessionValid) {
                setSessionToken(encodeURIComponent(storedSession!.session));
                return;
            }

            const createdSession = await googleTilesService.createSatelliteSession(authStore.publicGoogleAccessToken!);
            if (isRequestCancelled) return;

            localStorageService.setGoogleTilesSession({
                session: createdSession.session,
                expiry: Number(createdSession.expiry),
            });

            setSessionToken(encodeURIComponent(createdSession.session));
        })();

        return () => {
            isRequestCancelled = true;
        };
    }, [mapStore.isStyleLoaded, isStyleLoadedOverride, localStorageService, googleTilesService, authStore.publicGoogleAccessToken]);

    if (!sessionToken) return null;

    return (
        <Source id="satelliteGoogleBaseMapSource" type="raster" tiles={[
            `https://tile.googleapis.com/v1/2dtiles/{z}/{x}/{y}?session=${sessionToken}&key=${authStore.publicGoogleAccessToken}`,
        ]} tileSize={256} attribution="Google Maps">
            <Layer
                id="satelliteGoogleBaseMapLayer"
                type="raster"
                slot="top"
                beforeId="mapbox-satellite"
                layout={{ visibility: visible ? "visible" : "none" }}
            />
        </Source>
    );
}

export default observer(SatelliteGoogleBaseMapLayer);